import { useState, useEffect, useCallback, useRef } from 'react'

// 📡 État des câbles — santé des connecteurs / liens inter-machines.
// Interroge /api/connectors/health (checks côté serveur), auto-poll 15s + bouton Retester.
type Cable = {
  id: string; label: string; via: string; note?: string
  status: 'up' | 'down' | 'planned'
  code?: number | null; latency_ms?: number | null; error?: string
}

const DOT: Record<string, string> = { up: '#22c55e', down: '#ef4444', planned: '#6b7280' }

export default function CablesHealth() {
  const [cables, setCables] = useState<Cable[]>([])
  const [loading, setLoading] = useState(true)
  const [checkedAt, setCheckedAt] = useState<number | null>(null)
  const [open, setOpen] = useState(true)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/connectors/health')
      if (res.ok) {
        const d = await res.json()
        setCables(d.cables || [])
        setCheckedAt(d.checked_at || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    timer.current = setInterval(load, 15000)
    return () => { if (timer.current) clearInterval(timer.current) }
  }, [load])

  const testable = cables.filter(c => c.status !== 'planned')
  const up = testable.filter(c => c.status === 'up').length

  return (
    <div style={{ margin: '0 0 14px', border: '1px solid #1e293b', borderRadius: 10, background: '#0b1220' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>📡 État des câbles</span>
          <span style={{ fontSize: 11, color: up === testable.length ? '#22c55e' : '#f59e0b' }}>{up}/{testable.length} OK</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={(e) => { e.stopPropagation(); load() }}
            style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #374151', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 11 }}>
            {loading ? '…' : '🔄 Retester'}
          </button>
          <span style={{ fontSize: 15, color: '#64748b' }}>{open ? '▾' : '▸'}</span>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 8px 8px' }}>
          {cables.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderTop: '1px solid #111a2b' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: DOT[c.status], flexShrink: 0, boxShadow: c.status === 'up' ? `0 0 6px ${DOT.up}` : 'none' }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>{c.via}</span>
              <span style={{ fontSize: 11, minWidth: 96, textAlign: 'right', color: c.status === 'up' ? '#22c55e' : c.status === 'down' ? '#ef4444' : '#6b7280' }}>
                {c.status === 'up' ? `up · ${c.latency_ms}ms` : c.status === 'down' ? `down${c.error ? ' · ' + c.error : ''}` : (c.note || 'à venir')}
              </span>
            </div>
          ))}
          {checkedAt && (
            <div style={{ textAlign: 'right', fontSize: 10, color: '#475569', padding: '4px 8px 0' }}>
              maj {new Date(checkedAt * 1000).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
